import { atom, selector, selectorFamily } from 'recoil'
import {
  TrendsDatesResponse,
  TrendsPeriod,
  TrendsResponse,
  Polygon,
  amMobilityAggregatedMethodEnum,
  AMMobilityFrequency,
  amMobilityFrequencyEnum,
  AMMobilityAggregatedMethod,
  AMDateValue,
  Position,
  amMobilityProvidersEnum,
} from 'src/common-services'
import { alphamapClient, footfallClient } from 'src/clients'
import { DEFAULT_LRU_SIZE, keyFactory, retryAtom, uncachedSelector, uncachedSelectorFamily } from 'src/utils/recoil'
import { googlePlace, venueMatch } from 'src/atoms/search'
import { ifNotFound } from 'src/utils/ajax'
import {
  useArea,
  multipolygon,
  venuesInArea,
  MAX_VENUES,
  areaRadius,
  locationsInArea,
} from 'src/components/insights/common/visits/atoms/area'
import uniqBy from 'lodash/uniqBy'
import { CustomFootFallDateRange } from 'src/atoms/custom-polygon'

import * as matthewsAtoms from 'src/atoms/matthews'
import * as mobilityAtoms from 'src/atoms/mobility'
import { ProvidersTrendsStats } from 'src/atoms/mobility/visits-trends'
import { getAlphaMapOnlyProviderStats } from 'src/utils/mobility'
import { isPaidUser } from 'src/atoms/user'
import { convertDateToString } from 'src/utils/date-utils'

const key = keyFactory('insights-footfall-trends')

export const DEFAULT_TRENDS_SELECTED_RANGE = 24
export const DEFAULT_TRENDS_PERIOD = TrendsPeriod.MONTHLY
export const DEFAULT_AREA_TRENDS_TYPE = 'average'

export const showTrendsSelectedRange = atom<boolean>({
  key: key('show-trends-selected-range'),
  default: true,
})

export const showTrendsPeriod = atom<boolean>({
  key: key('show-trends-period'),
  default: true,
})

export const trendsPeriod = atom<TrendsPeriod>({
  key: key('trends-period'),
  default: DEFAULT_TRENDS_PERIOD,
})

export const trendsFrequency = atom<AMMobilityFrequency>({
  key: key('trends-frequency'),
  default: amMobilityFrequencyEnum.monthly,
})

export const trendsAggregation = atom<AMMobilityAggregatedMethod>({
  key: key('trends-aggregation'),
  default: amMobilityAggregatedMethodEnum.avg,
})

export const trendDatesByPeriod = selectorFamily<TrendsDatesResponse, TrendsPeriod>({
  key: key('trends-dates-by-period'),
  get:
    (period) =>
    ({ get }) => {
      get(retryAtom)
      return footfallClient.getTrendsDates({ period })
    },
})

export const trendsSelectedRange = atom<number>({
  key: key('trends-selected-range'),
  default: DEFAULT_TRENDS_SELECTED_RANGE,
})

export const areaTrendsType = atom<'average' | 'total'>({
  key: key('area-trends-type'),
  default: DEFAULT_AREA_TRENDS_TYPE,
})

export const validDateRange = selector<CustomFootFallDateRange>({
  key: key('valid-date=range'),
  get: async ({ get }) => {
    const id = get(mobilityAtoms.alphamapLocationId)
    if (!id) {
      return get(mobilityAtoms.validDateRangeString)
    }
    const validRange = get(
      mobilityAtoms.validTTADateRangeById({
        id,
        provider: amMobilityProvidersEnum.alphamap,
      })
    )

    return {
      start: convertDateToString(validRange.start),
      end: convertDateToString(validRange.end),
    }
  },
})

export const trendsSelectedDates = atom<CustomFootFallDateRange>({
  key: key('trends-selected-date'),
  default: validDateRange,
})

export const mockVenueTrends = atom<TrendsResponse | undefined>({
  key: key('mock-venue-trends'),
  default: undefined,
})

const venueTrends = uncachedSelector<TrendsResponse | undefined>({
  key: key('venue-trends'),
  get: async ({ get }) => {
    if (process.env.NODE_ENV === 'development') {
      // for testing
      if (get(mockVenueTrends)) {
        return get(mockVenueTrends)
      }
    }

    const isIframe = get(matthewsAtoms.isIframe)
    get(retryAtom)
    const isPaid = get(isPaidUser)
    if (!isIframe && !isPaid) {
      return []
    }
    const { start, end } = get(trendsSelectedDates)
    const trendsPeriodValue = get(trendsPeriod)
    const venue = get(venueMatch)
    if (!venue) {
      return undefined
    }

    const items = await ifNotFound(
      footfallClient.getTrends({
        start,
        end,
        period: trendsPeriodValue,
        venueId: venue.venueId,
      }),
      undefined
    )
    // TODO(bmc): this will not be needed once v2 of foottraffic data is imported
    return uniqBy(items, 'date')
  },
  lru: DEFAULT_LRU_SIZE,
})

export type TrendsDatum = {
  // keep this a string because dates don't serialize via the print boundary
  date: string
  value: number
}

export const chartData = selector<TrendsDatum[]>({
  key: key('chart-data'),
  get: ({ get }) => {
    const customArea = get(useArea)
    if (!customArea) {
      return (get(venueTrends) ?? []).map((val) => ({ date: val.date, value: val.personCountSum }))
    }
    return get(areaTrends) ?? []
  },
})

export const bothTrendsChartData = selector({
  key: key('both-trends-chart-data'),
  get: async ({ get }) => {
    const shape = get(multipolygon)
    const coordinates = shape.coordinates[0]
    const { start: from, end: to } = get(trendsSelectedDates)
    const frequency = get(trendsFrequency)

    const polygon: Polygon = {
      type: 'Polygon',
      coordinates,
    }
    try {
      const avgPromise = alphamapClient.mobility.getPolygonVisits({
        polygon,
        frequency,
        from,
        to,
        method: amMobilityAggregatedMethodEnum.avg,
      })
      const totalPromise = alphamapClient.mobility.getPolygonVisits({
        polygon,
        frequency,
        from,
        to,
        method: amMobilityAggregatedMethodEnum.total,
      })
      const [avg, total] = await Promise.all([avgPromise, totalPromise])
      return {
        total,
        avg,
      }
    } catch (e) {
      return undefined
    }
  },
})

export const totalLocationsInArea = selector<number>({
  key: key('total-locations-in-area'),
  get: ({ get }) => {
    const locationsForCurrent = get(locationsInArea)
    const totalLocations =
      'totalLocations' in locationsForCurrent ? locationsForCurrent.totalLocations : locationsForCurrent.length
    return totalLocations ?? 0
  },
})

/** Converts alphamapLocation or googlePlace into a location for the legend */
export const currentLocationForLegend = selector<mobilityAtoms.AMMobilityLocation | undefined>({
  key: key('current-location-for-legend'),
  get: ({ get }) => {
    const currentLocation = get(mobilityAtoms.alphamapLocation)
    if (currentLocation) {
      return {
        ...currentLocation,
        name: 'Locations in Area',
      }
    }

    const place = get(googlePlace)
    if (place) {
      return convertToLocationForLegend(place.geometry.location, place.formatted_address)
    }

    const customArea = get(useArea)
    if (customArea) {
      return convertToLocationForLegend([], '')
    }

    return undefined
  },
})

export const currentLocationStats = selector<ProvidersTrendsStats | undefined>({
  key: key('current-location-stats'),
  get: ({ get }) => {
    const currentLocation = get(currentLocationForLegend)
    const data = get(chartData)
    if (!currentLocation) {
      return undefined
    }

    const stat = getTrendsStats(data)
    return stat
  },
})

export const areaTrendsError = uncachedSelector<boolean>({
  key: key('area-trends-error'),
  get: ({ get }) => {
    const isIframe = get(matthewsAtoms.isIframe)

    if (isIframe) {
      return false
    }

    const place = get(googlePlace)
    const radius = get(areaRadius)
    const areaTrendsValue = get(areaTrends)
    if (!place || radius || areaTrendsValue) {
      return false
    }
    return true
  },
})

export const areaTrends = uncachedSelector<AMDateValue[]>({
  key: key('area-trends'),
  get: async ({ get }) => {
    const shape = get(multipolygon)
    return get(areaTrendsByPolygon(shape.coordinates[0]))
  },
  dangerouslyAllowMutability: true,
  lru: DEFAULT_LRU_SIZE,
})

export const areaTrendsByPolygon = uncachedSelectorFamily<AMDateValue[], Position[][]>({
  key: key('area-trends-by-polygon'),
  get:
    (coordinates) =>
    async ({ get }) => {
      const isIframe = get(matthewsAtoms.isIframe)
      const isPaid = get(isPaidUser)
      get(retryAtom)
      if (!isIframe && !isPaid) {
        return []
      }

      const { start: from, end: to } = get(trendsSelectedDates)
      const frequency = get(trendsFrequency)
      const method = get(trendsAggregation)

      const polygon: Polygon = {
        type: 'Polygon',
        coordinates,
      }
      try {
        const items = await alphamapClient.mobility.getPolygonVisits({
          polygon,
          frequency,
          from,
          to,
          method,
        })
        return items
      } catch (e) {
        return []
      }
    },
  dangerouslyAllowMutability: true,
  lru: DEFAULT_LRU_SIZE,
})

export const areaVenueCount = uncachedSelector<number | string>({
  key: key('area-venue-count'),
  get: ({ get }) => {
    if (!get(useArea)) {
      return 0
    }
    const length = get(venuesInArea).length
    return length >= MAX_VENUES ? `${MAX_VENUES}+` : length
  },
  lru: DEFAULT_LRU_SIZE,
})

const getTrendsStats = (data: TrendsDatum[]): ProvidersTrendsStats => {
  const avg = data.length > 0 ? Math.floor(data.reduce((acc, curr) => acc + curr.value, 0) / data.length) : 0
  const total = data.reduce((acc, curr) => acc + curr.value, 0)

  const stats = getAlphaMapOnlyProviderStats({
    averageVisits: avg,
    totalVisits: total,
  })

  return stats
}

const convertToLocationForLegend = (coordinates: number[], street: string) => {
  const location: mobilityAtoms.AMMobilityLocation = {
    name: 'Locations in Area',
    city: '',
    country: '',
    id: '',
    latLng: {
      coordinates,
      type: 'Point',
    },
    naicsCode: '',
    state: '',
    zipCode: '',
    street,
    isOpen: true,
  }
  return location
}
